import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Settings,
  Mail,
  CreditCard,
  Shield,
  Globe,
  Save,
  RefreshCw,
  TestTube,
  AlertTriangle
} from 'lucide-react';
import { api } from '../auth/authStore';
import { SMTPSettingsForm } from './components/SMTPSettingsForm';
import { PaymentGatewayForm } from './components/PaymentGatewayForm';
import { GeneralSettingsForm } from './components/GeneralSettingsForm';
import { SecuritySettingsForm } from './components/SecuritySettingsForm';

// Types
export interface SMTPConfig {
  enabled: boolean;
  host: string;
  port: number;
  secure: boolean;
  auth: {
    user: string;
    pass: string;
  };
  from: {
    email: string;
    name: string;
  };
  replyTo?: string;
  testMode: boolean;
}

export interface PaymentGatewayConfig {
  enabled: boolean;
  provider: 'stripe' | 'paypal' | 'moneris' | 'bambora';
  mode: 'test' | 'live';
  currency: 'CAD' | 'USD';
  stripe?: {
    publishableKey: string;
    secretKey: string;
    webhookSecret: string;
    accountId?: string;
  };
  paypal?: {
    clientId: string;
    clientSecret: string;
    webhookId: string;
  };
  moneris?: {
    storeId: string;
    apiToken: string;
    environment: 'qa' | 'prod';
  };
  bambora?: {
    merchantId: string;
    apiKey: string;
    environment: 'sandbox' | 'production';
  };
  taxConfig: {
    gstRate: number;
    pstRates: { [province: string]: number };
    hstProvinces: string[];
    hstRate: number;
  };
}

export interface GeneralConfig {
  siteName: string;
  siteUrl: string;
  supportEmail: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  language: string;
  security: SecurityConfig;
  features: {
    tenantRegistration: boolean;
    multiTenancy: boolean;
    apiAccess: boolean;
    webhooks: boolean;
    analytics: boolean;
    auditLogs: boolean;
  };
  maintenance: {
    enabled: boolean;
    message: string;
    allowedIPs: string[];
    scheduledStart?: string;
    scheduledEnd?: string;
  };
}

export interface SecurityConfig {
  sessionTimeout: number;
  roleBasedTimeouts: {
    SuperAdmin: number;
    FirmAdmin: number;
    Manager: number;
    Staff: number;
    Client: number;
  };
  sessionWarningMinutes: number;
  rememberMeEnabled: boolean;
  rememberMeDuration: number;
  activityBasedExtension: boolean;
  maxLoginAttempts: number;
  lockoutDuration: number;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSymbols: boolean;
    maxAge: number;
  };
  mfaRequired: boolean;
  allowedIPs: string[];
}

export interface PlatformSettings {
  _id: string;
  smtp: SMTPConfig;
  paymentGateway: PaymentGatewayConfig;
  general: GeneralConfig;
  version: number;
  createdAt: string;
  updatedAt: string;
}

const PlatformSettings: React.FC = () => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'smtp' | 'payment' | 'security'>('general');
  const [hasChanges, setHasChanges] = useState(false);

  // Tab configuration
  const tabs = [
    { id: 'general' as const, label: 'General', icon: Globe },
    { id: 'smtp' as const, label: 'SMTP Settings', icon: Mail },
    { id: 'payment' as const, label: 'Payment Gateway', icon: CreditCard },
    { id: 'security' as const, label: 'Security', icon: Shield },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/settings');

      if (response.data.success) {
        // Ensure all nested objects have proper default values to prevent uncontrolled component warnings
        const settingsData = response.data.data;
        const normalizedSettings = {
          ...settingsData,
          smtp: {
            enabled: false,
            host: '',
            port: 587,
            secure: false,
            auth: {
              user: '',
              pass: ''
            },
            from: {
              email: '',
              name: ''
            },
            replyTo: '',
            testMode: false,
            ...settingsData.smtp
          },
          paymentGateway: {
            enabled: false,
            provider: 'stripe',
            mode: 'test',
            currency: 'CAD',
            stripe: {
              publishableKey: '',
              secretKey: '',
              webhookSecret: '',
              accountId: ''
            },
            paypal: {
              clientId: '',
              clientSecret: '',
              webhookId: ''
            },
            moneris: {
              storeId: '',
              apiToken: '',
              environment: 'qa'
            },
            bambora: {
              merchantId: '',
              apiKey: '',
              environment: 'sandbox'
            },
            taxConfig: {
              gstRate: 0.05,
              pstRates: {},
              hstProvinces: [],
              hstRate: 0.13
            },
            ...settingsData.paymentGateway
          },
          general: {
            siteName: '',
            siteUrl: '',
            supportEmail: '',
            timezone: 'UTC',
            dateFormat: 'MM/DD/YYYY',
            currency: 'USD',
            language: 'en',
            security: {
              sessionTimeout: 480,
              roleBasedTimeouts: {
                SuperAdmin: 240,
                FirmAdmin: 360,
                Manager: 480,
                Staff: 480,
                Client: 720,
              },
              sessionWarningMinutes: 15,
              rememberMeEnabled: true,
              rememberMeDuration: 30,
              activityBasedExtension: true,
              maxLoginAttempts: 5,
              lockoutDuration: 30,
              passwordPolicy: {
                minLength: 8,
                requireUppercase: true,
                requireLowercase: true,
                requireNumbers: true,
                requireSymbols: false,
                maxAge: 90
              },
              mfaRequired: false,
              allowedIPs: []
            },
            features: {
              tenantRegistration: true,
              multiTenancy: true,
              apiAccess: false,
              webhooks: false,
              analytics: true,
              auditLogs: true
            },
            maintenance: {
              enabled: false,
              message: '',
              allowedIPs: []
            },
            ...settingsData.general
          }
        };

        setSettings(normalizedSettings);
      } else {
        throw new Error(response.data.message || 'Failed to fetch platform settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings || !hasChanges) return;

    try {
      setSaving(true);
      const response = await api.put('/platform/settings', {
        smtp: settings.smtp,
        paymentGateway: settings.paymentGateway,
        general: settings.general,
        version: settings.version,
      });

      if (response.data.success) {
        setSettings(response.data.data);
        setHasChanges(false);
        toast.success('Platform settings saved successfully');
      } else {
        throw new Error(response.data.message || 'Failed to save settings');
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/platform/settings/reset');

      if (response.data.success) {
        setSettings(response.data.data);
        setHasChanges(false);
        toast.success('Platform settings reset to defaults');
      } else {
        throw new Error(response.data.message || 'Failed to reset settings');
      }
    } catch (error: any) {
      console.error('Error resetting settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (section: keyof PlatformSettings, updates: any) => {
    if (!settings) return;

    setSettings(prev => prev ? {
      ...prev,
      [section]: {
        ...prev[section],
        ...updates,
      },
    } : null);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading platform settings...</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlertTriangle className="w-8 h-8 text-red-500" />
        <span className="ml-2 text-gray-600">Failed to load platform settings</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Settings className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
              <p className="text-gray-600 mt-1">Configure platform-wide settings and integrations</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        
        {hasChanges && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-800">You have unsaved changes</span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && (
            <GeneralSettingsForm
              settings={settings.general}
              onUpdate={(updates) => updateSettings('general', updates)}
            />
          )}
          {activeTab === 'smtp' && (
            <SMTPSettingsForm
              settings={settings.smtp}
              onUpdate={(updates) => updateSettings('smtp', updates)}
            />
          )}
          {activeTab === 'payment' && (
            <PaymentGatewayForm
              settings={settings.paymentGateway}
              onUpdate={(updates) => updateSettings('paymentGateway', updates)}
            />
          )}
          {activeTab === 'security' && (
            <SecuritySettingsForm
              settings={settings.general.security}
              onUpdate={(updates) => updateSettings('general', { security: { ...settings.general.security, ...updates } })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
